// File: src/pages/reports/GSTR3B1.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Button, Input, Spinner } from "reactstrap";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ImFileExcel, ImFilePdf } from "react-icons/im";

// ===== your modules (all via ../import/*) =====
import { PurchaseOrderList, _Get } from "../import/ApiEndPoint/ApiCalling";
import { view_create_order_history_database } from "../import/ApiEndPoint/Api";
import { baseURL } from "../import/ApiEndPoint/TestApi";
import { CheckPermission } from "../import/CheckPermission";

// ===== Inline styling (single-line header + existing table look) =====
const injectOnce = () => {
  if (document.getElementById("gstr3b-inline-css")) return;

  const css = `
    /* ===== Header (single line, minimal) ===== */
    .g3HdrCard{
      border:1px solid #e5e7eb;
      background:#ffffff;
      border-radius:16px;
      padding:10px 12px;
      box-shadow: 0 6px 18px rgba(15,23,42,.05);
    }

    .g3HdrRow{
      display:flex;
      align-items:flex-end;
      gap:10px;
      flex-wrap:nowrap;           /* one line */
      overflow-x:auto;            /* scroll if small */
      -webkit-overflow-scrolling: touch;
      padding-bottom:2px;
    }
    .g3HdrRow::-webkit-scrollbar{height:6px}
    .g3HdrRow::-webkit-scrollbar-thumb{background:#e5e7eb;border-radius:999px}

    .g3Field{
      display:flex;
      flex-direction:column;
      gap:4px;
      min-width:170px;
    }
    .g3Field.small{min-width:140px}
    .g3Field.wide{min-width:230px}
    .g3Field.range{min-width:260px}

    .g3Lbl{
      font-size:11px;
      color:#64748b;
      font-weight:800;
      letter-spacing:.02em;
      line-height:1;
      margin:0;
      text-transform:uppercase;
      white-space:nowrap;
    }

    .g3Ctl{
      height:34px !important;
      border-radius:12px !important;
      border:1px solid #e5e7eb !important;
      padding:6px 10px !important;
      background:#fff;
    }

    .g3RowInline{
      display:flex;
      gap:8px;
      align-items:center;
      flex-wrap:nowrap;
    }

    .g3IconBtn{
      height:34px;
      width:42px;
      border-radius:12px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      padding:0;
    }
    .g3IconBtn svg{font-size:18px}

    .g3Spacer{flex:1; min-width:16px}

    /* ===== Existing table styling (kept same) ===== */
    .list-card{border:1px solid #e5e7eb;border-radius:14px;padding:8px}
    .wl-table-wrap{width:100%;overflow:auto;border:1px solid #e5e7eb;border-radius:14px;background:#fff}
    .wl-table{width:100%;border-collapse:collapse;min-width:900px}
    .wl-table thead th{
      background:#f4f7f6;color:#0f172a;font-weight:700;text-transform:uppercase;
      font-size:12px;letter-spacing:.03em;border-bottom:1px solid #e5e7eb;
      padding:8px;white-space:nowrap;text-align:center;position:sticky;top:0;z-index:1
    }
    .wl-table tbody td{
      padding:8px;border-bottom:1px solid #f1f5f9;color:#0f172a;font-size:14px;text-align:center
    }
    .wl-table tbody tr:hover{background:#f9fbff}
  `;

  const style = document.createElement("style");
  style.id = "gstr3b-inline-css";
  style.innerHTML = css;
  document.head.appendChild(style);
};

const tdRight = { textAlign: "right" };

const getSelectedDatabase = (db) => {
  const financialYearSelected = localStorage.getItem("financialYearSelected");
  const cleanDb = String(db || "").trim();

  if (!cleanDb) return "";

  // If database is already in FY format like abc-2026-27, do not append again.
  if (/^-?\d{4}-\d{2}$/.test(cleanDb.split("-").slice(-2).join("-"))) {
    return cleanDb;
  }

  return financialYearSelected
    ? `${cleanDb}-${financialYearSelected}`
    : cleanDb;
};

const extractSalesOrders = (res = {}) => {
  if (Array.isArray(res?.orderHistory)) return res.orderHistory;
  if (Array.isArray(res?.OrderHistory)) return res.OrderHistory;
  if (Array.isArray(res?.data?.orderHistory)) return res.data.orderHistory;

  const bucketed = [
    ...(Array.isArray(res?.b2bData) ? res.b2bData : []),
    ...(Array.isArray(res?.b2csData) ? res.b2csData : []),
    ...(Array.isArray(res?.b2clData) ? res.b2clData : []),
  ];

  if (bucketed.length) return bucketed;

  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.CreateOrder)) return res.CreateOrder;
  if (Array.isArray(res?.orders)) return res.orders;

  return [];
};

export default function GSTR3B1() {
  const [loading, setLoading] = useState(true);

  // filters
  const [yearFilter, setYearFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [startMonthFilter, setStartMonthFilter] = useState("");
  const [endMonthFilter, setEndMonthFilter] = useState("");

  // row filter inputs
  const [filterValue, setFilterValue] = useState("");
  const [filterTaxableValue, setFilterTaxableValue] = useState("");

  // purchases (reverse charge bucket)
  const [pTaxable, setPTaxable] = useState(0);
  const [pIGST, setPIGST] = useState(0);
  const [pCGST, setPCGST] = useState(0);
  const [pSGST, setPSGST] = useState(0);

  // sales (outward supplies)
  const [sTaxable, setSTaxable] = useState(0);
  const [sIGST, setSIGST] = useState(0);
  const [sCGST, setSCGST] = useState(0);
  const [sSGST, setSSGST] = useState(0);

  const [perm, setPerm] = useState({ View: true, Download: true });

  useEffect(() => {
    injectOnce();
    setPerm(CheckPermission("GSTR 3B") || { View: true, Download: true });
  }, []);

  // pull + aggregate
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchSales(), fetchPurchases()]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter, startMonthFilter, endMonthFilter, yearFilter]);

  const inRange = (d) => {
    const dt = new Date(d);
    const m = dt.getMonth() + 1;
    const y = dt.getFullYear();
    if (yearFilter && y !== Number(yearFilter)) return false;
    if (monthFilter && m !== Number(monthFilter)) return false;
    if (startMonthFilter && endMonthFilter) {
      if (m < Number(startMonthFilter) || m > Number(endMonthFilter))
        return false;
    }
    return true;
  };

  const safeSum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);

  const fetchSales = async () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const selectedDatabase = getSelectedDatabase(user?.database);

    const res = await _Get(
      view_create_order_history_database,
      selectedDatabase,
    );

    const completed = extractSalesOrders(res).filter(
      (o) => String(o?.status || "").toLowerCase() === "completed",
    );
    const filtered = completed.filter((o) => inRange(o?.date));

    // sum by order items
    const taxable = safeSum(
      filtered.map((o) =>
        safeSum((o.orderItems || []).map((i) => i.taxableAmount)),
      ),
    );
    const igst = safeSum(
      filtered.map((o) => safeSum((o.orderItems || []).map((i) => i.igstRate))),
    );
    const sgst = safeSum(
      filtered.map((o) => safeSum((o.orderItems || []).map((i) => i.sgstRate))),
    );
    const cgst = safeSum(
      filtered.map((o) => safeSum((o.orderItems || []).map((i) => i.cgstRate))),
    );

    setSTaxable(taxable);
    setSIGST(igst);
    setSSGST(sgst);
    setSCGST(cgst);
  };

  const fetchPurchases = async () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const selectedDatabase = getSelectedDatabase(user?.database);

    const res = await PurchaseOrderList(user?._id, selectedDatabase);
    const orders = Array.isArray(res?.orderHistory) ? res.orderHistory : [];
    const filtered = orders.filter((o) => inRange(o?.date));

    const taxable = safeSum(
      filtered.map((o) =>
        safeSum((o.orderItems || []).map((i) => i.taxableAmount)),
      ),
    );
    const igst = safeSum(
      filtered.map((o) => safeSum((o.orderItems || []).map((i) => i.igstRate))),
    );
    const sgst = safeSum(
      filtered.map((o) => safeSum((o.orderItems || []).map((i) => i.sgstRate))),
    );
    const cgst = safeSum(
      filtered.map((o) => safeSum((o.orderItems || []).map((i) => i.cgstRate))),
    );

    setPTaxable(taxable);
    setPIGST(igst);
    setPSGST(sgst);
    setPCGST(cgst);
  };

  // rows (Nature of supplies + Eligible ITC)
  const natureOfSupplies = useMemo(
    () => [
      {
        label:
          "Outward Taxable Supplies (other than zero-rated, nil-rated, and exempted)",
        taxableValue: sTaxable,
        integratedTax: sIGST,
        centralTax: sCGST,
        stateTax: sSGST,
        cess: 0,
      },
      {
        label: "Outward Taxable Supplies (zero-rated)",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
      {
        label: "Other Outward Taxable Supplies (Nil-rated, exempted)",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
      {
        label: "Inward Supplies (liable to reverse charge)",
        taxableValue: pTaxable,
        integratedTax: pIGST,
        centralTax: pCGST,
        stateTax: pSGST,
        cess: 0,
      },
      {
        label: "Non-GST Outward Supplies",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
      {
        label: "Total",
        taxableValue: sTaxable + pTaxable,
        integratedTax: sIGST + pIGST,
        centralTax: sCGST + pCGST,
        stateTax: sSGST + pSGST,
        cess: 0,
      },
    ],
    [sTaxable, sIGST, sCGST, sSGST, pTaxable, pIGST, pCGST, pSGST],
  );

  const eligibleITC = useMemo(
    () => [
      {
        label: "Import of Goods",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
      {
        label: "Import of Services",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
      {
        label:
          "Inward Supplies liable to reverse charge (other than 1 & 2 above)",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
      {
        label: "Inward Supplies from ISD",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
      {
        label: "All Other ITC",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
      {
        label: "Total",
        taxableValue: 0,
        integratedTax: 0,
        centralTax: 0,
        stateTax: 0,
        cess: 0,
      },
    ],
    [],
  );

  // row search / min taxable filter
  const filteredNature = useMemo(() => {
    const minTax = Number(filterTaxableValue) || 0;
    const term = (filterValue || "").toLowerCase().trim();
    return natureOfSupplies.filter(
      (r) =>
        r.label.toLowerCase().includes(term) &&
        Number(r.taxableValue) >= minTax,
    );
  }, [filterValue, filterTaxableValue, natureOfSupplies]);

  // ===== Exports =====
  const exportXLS = () => {
    const a1 = [
      [
        "Nature of Supplies",
        "Taxable Value",
        "Integrated Tax",
        "Central Tax",
        "State Tax",
        "Cess",
      ],
      ...natureOfSupplies.map((r) => [
        r.label,
        Number(r.taxableValue).toFixed(2),
        Number(r.integratedTax).toFixed(2),
        Number(r.centralTax).toFixed(2),
        Number(r.stateTax).toFixed(2),
        Number(r.cess).toFixed(2),
      ]),
      [],
      [
        "Eligible ITC",
        "Taxable Value",
        "Integrated Tax",
        "Central Tax",
        "State Tax",
        "Cess",
      ],
      ...eligibleITC.map((r) => [
        r.label,
        Number(r.taxableValue).toFixed(2),
        Number(r.integratedTax).toFixed(2),
        Number(r.centralTax).toFixed(2),
        Number(r.stateTax).toFixed(2),
        Number(r.cess).toFixed(2),
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(a1);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GSTR-3B Summary");
    XLSX.writeFile(wb, "GSTR3B_Summary.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const company = JSON.parse(localStorage.getItem("Companydetail") || "{}");

    const run = () => {
      doc.setFontSize(14);
      doc.text("GSTR-3B Summary", 14, 20);

      const nos = natureOfSupplies.map((r) => [
        r.label,
        Number(r.taxableValue).toFixed(2),
        Number(r.integratedTax).toFixed(2),
        Number(r.centralTax).toFixed(2),
        Number(r.stateTax).toFixed(2),
        Number(r.cess).toFixed(2),
      ]);
      doc.autoTable({
        head: [
          [
            "Nature of Supplies",
            "Taxable Value",
            "Integrated Tax",
            "Central Tax",
            "State Tax",
            "Cess",
          ],
        ],
        body: nos,
        startY: 28,
        styles: {
          fontSize: 9,
          cellPadding: 3,
          halign: "center",
          valign: "middle",
        },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
        alternateRowStyles: { fillColor: [247, 249, 255] },
      });

      const itc = eligibleITC.map((r) => [
        r.label,
        Number(r.taxableValue).toFixed(2),
        Number(r.integratedTax).toFixed(2),
        Number(r.centralTax).toFixed(2),
        Number(r.stateTax).toFixed(2),
        Number(r.cess).toFixed(2),
      ]);
      doc.autoTable({
        head: [
          [
            "Eligible ITC",
            "Taxable Value",
            "Integrated Tax",
            "Central Tax",
            "State Tax",
            "Cess",
          ],
        ],
        body: itc,
        startY: doc.lastAutoTable.finalY + 10,
        styles: {
          fontSize: 9,
          cellPadding: 3,
          halign: "center",
          valign: "middle",
        },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
        alternateRowStyles: { fillColor: [247, 249, 255] },
      });

      doc.save("GSTR3B_Summary.pdf");
    };

    // try to place logo (non-blocking)
    if (company?.logo) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          doc.addImage(img, "JPEG", 80, 6, 50, 12);
        } catch {}
        run();
      };
      img.onerror = run;
      img.src = `${baseURL}/Images/${company.logo}`;
    } else {
      run();
    }
  };

  // ===== UI =====
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const totalRows = natureOfSupplies.length + eligibleITC.length;

  return (
    <>
      {/* ===== HEADER (single line, no title/pill/back) ===== */}
      <Row className="mt-1">
        <Col sm="12">
          <Card className="g3HdrCard">
            <div className="g3HdrRow">
              {/* <div className="g3Field small">
                <div className="g3Lbl">Year</div>
                <Input
                  type="select"
                  className="g3Ctl"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </Input>
              </div> */}

              <div className="g3Field wide">
                <div className="g3Lbl">Month</div>
                <Input
                  type="select"
                  className="g3Ctl"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map((m, i) => (
                    <option key={i + 1} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </Input>
              </div>

              <div className="g3Field range">
                <div className="g3Lbl">Range (From / To)</div>
                <div className="g3RowInline">
                  <Input
                    type="select"
                    className="g3Ctl"
                    value={startMonthFilter}
                    onChange={(e) => setStartMonthFilter(e.target.value)}
                    style={{ width: 120 }}
                  >
                    <option value="">From</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Input>
                  <Input
                    type="select"
                    className="g3Ctl"
                    value={endMonthFilter}
                    onChange={(e) => setEndMonthFilter(e.target.value)}
                    style={{ width: 120 }}
                  >
                    <option value="">To</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Input>
                </div>
              </div>

              <div className="g3Field wide">
                <div className="g3Lbl">Search Label</div>
                <Input
                  className="g3Ctl"
                  placeholder="Filter rows…"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                />
              </div>

              <div className="g3Field small">
                <div className="g3Lbl">Min Taxable</div>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="g3Ctl"
                  value={filterTaxableValue}
                  onChange={(e) => setFilterTaxableValue(e.target.value)}
                />
              </div>

              <div className="g3Spacer" />

              {perm?.Download && (
                <>
                  <Button
                    outline
                    color="primary"
                    className="g3IconBtn"
                    onClick={exportPDF}
                    title="Download PDF"
                  >
                    <ImFilePdf />
                  </Button>
                  <Button
                    outline
                    color="primary"
                    className="g3IconBtn"
                    onClick={exportXLS}
                    title="Download Excel"
                  >
                    <ImFileExcel />
                  </Button>
                </>
              )}

              {/* minimal count on far right */}
              <div
                style={{
                  paddingBottom: 6,
                  fontWeight: 900,
                  color: "#0f172a",
                  whiteSpace: "nowrap",
                }}
                title="Total rows"
              >
                {totalRows}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== CARD: Tables ===== */}
      <Row className="mt-1">
        <Col sm="12">
          <Card className="list-card">
            {/* Nature of Supplies */}
            <div className="wl-table-wrap">
              <table className="wl-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", paddingLeft: 12 }}>
                      Nature of Supplies
                    </th>
                    <th>Taxable Value</th>
                    <th>Integrated Tax</th>
                    <th>Central Tax</th>
                    <th>State Tax</th>
                    <th>Cess</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNature.map((r, idx) => (
                    <tr key={`nos-${idx}`}>
                      <td style={{ textAlign: "left", paddingLeft: 12 }}>
                        {r.label}
                      </td>
                      <td style={tdRight}>
                        {Number(r.taxableValue).toFixed(2)}
                      </td>
                      <td style={tdRight}>
                        {Number(r.integratedTax).toFixed(2)}
                      </td>
                      <td style={tdRight}>{Number(r.centralTax).toFixed(2)}</td>
                      <td style={tdRight}>{Number(r.stateTax).toFixed(2)}</td>
                      <td style={tdRight}>{Number(r.cess).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Eligible ITC */}
            <div className="wl-table-wrap" style={{ marginTop: 16 }}>
              <table className="wl-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", paddingLeft: 12 }}>
                      Eligible ITC
                    </th>
                    <th>Taxable Value</th>
                    <th>Integrated Tax</th>
                    <th>Central Tax</th>
                    <th>State Tax</th>
                    <th>Cess</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleITC.map((r, idx) => (
                    <tr key={`itc-${idx}`}>
                      <td style={{ textAlign: "left", paddingLeft: 12 }}>
                        {r.label}
                      </td>
                      <td style={tdRight}>
                        {Number(r.taxableValue).toFixed(2)}
                      </td>
                      <td style={tdRight}>
                        {Number(r.integratedTax).toFixed(2)}
                      </td>
                      <td style={tdRight}>{Number(r.centralTax).toFixed(2)}</td>
                      <td style={tdRight}>{Number(r.stateTax).toFixed(2)}</td>
                      <td style={tdRight}>{Number(r.cess).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}
